/* ==========================================================================
   AB TEST — MOBILE FILTER BEHAVIOR
   Two independent variants live in this one file, each scoped under
   [data-filter-variant] on <html> and inert unless that value is present:
     - VERSION ONE: [data-filter-variant='v1']
     - VERSION TWO: [data-filter-variant='v2']
   Both work alongside the existing drawer script in
   snippets/custom-collection-filters.liquid without editing it: group-header
   clicks are intercepted in the capture phase so each variant's own
   open/close behavior runs instead of the default "one group open at a
   time" behavior.

   Priority order:
     1. Convert.com's decision (window.PS_FILTER_VARIANT), or the `?filter=`
        QA override — whichever resolves first wins, exactly as before.
     2. Only if Convert gives no answer at all: fall back to the merchant
        toggles in Theme Settings > A/B Test - Filter
        (window.PS_FILTER_V1_ENABLED / PS_FILTER_V2_ENABLED).
          - Only ONE toggle ON  -> that version shows permanently.
          - Both ON or both OFF -> default filter shows (no forced winner).

   Assignment: `window.PS_FILTER_VARIANT = 'v1'` or `'v2'` (set before this
   file runs), or `?filter=v1|v2` for QA (`?filter=off` control, `?filter=reset`
   clears).
   ========================================================================== */
/* Shared mobile gate — both variants below are inert unless the viewport is
   at or under this breakpoint (matches the drawer's own CSS breakpoint).
   Read live via .matches on every check, so resizing across it (rotating a
   device, dragging a desktop window narrower) is picked up immediately
   without needing a resize listener of our own. */
var PS_FILTER_MOBILE_MQL = window.matchMedia('(max-width: 991px)');
function psFilterIsMobile() {
  return PS_FILTER_MOBILE_MQL.matches;
}

(function () {
  'use strict';

  var STORAGE_KEY = 'ps_ab_filter';

  function normalize(value) {
    if (value == null) return null;
    var v = String(value).toLowerCase().trim();
    if (v === 'off' || v === 'control' || v === 'original' || v === '0') return 'control';
    v = v.replace(/^(variant|version|option|v)/, '');
    if (v === '1' || v === '2') return 'v' + v;
    return null;
  }

  var stored = (function () {
    try {
      var raw = new URLSearchParams(location.search).get('filter');
      if (raw != null) {
        var cleaned = String(raw).toLowerCase().trim();
        if (cleaned === 'reset' || cleaned === 'clear') {
          sessionStorage.removeItem(STORAGE_KEY);
        } else {
          var next = normalize(cleaned);
          if (next) sessionStorage.setItem(STORAGE_KEY, next);
        }
      }
      return sessionStorage.getItem(STORAGE_KEY);
    } catch (e) {
      return null;
    }
  })();

  var convertResult = normalize(stored) || normalize(window.PS_FILTER_VARIANT);

  var ACTIVE;
  if (convertResult) {
    ACTIVE = convertResult;
  } else {
    var v1Enabled = window.PS_FILTER_V1_ENABLED !== false;
    var v2Enabled = window.PS_FILTER_V2_ENABLED !== false;

    if (v1Enabled && !v2Enabled) {
      ACTIVE = 'v1';
    } else if (v2Enabled && !v1Enabled) {
      ACTIVE = 'v2';
    } else {
      ACTIVE = 'control';
    }
  }

  document.documentElement.setAttribute('data-filter-variant', ACTIVE);
  // control -> attribute is 'control' -> neither [data-filter-variant='v1'] nor
  // ='v2' matches -> default filter shows, untouched by either variant
})();


/* ==========================================================================
   VERSION ONE — groups expand independently and simultaneously
   ========================================================================== */
(function () {
  var DRAWER_ID = 'custom-mobile-filter-drawer';

  function isVersionOne() {
    return psFilterIsMobile() && document.documentElement.getAttribute('data-filter-variant') === 'v1';
  }

  function expandAllGroups(drawer) {
    drawer.querySelectorAll('.filter-drawer-group').forEach(function (group) {
      group.setAttribute('drawer-group-open', 'true');
      var header = group.querySelector('.filter-drawer-group-header[data-group-header]');
      if (header) header.setAttribute('aria-expanded', 'true');

      // The swatch/checkbox list only renders once the inner <details> is open
      // (independent of the outer group toggle) — open it too.
      var details = group.querySelector('details.custom-filter-group');
      if (details && !details.open) details.open = true;

      // "£56 - £85" reads as "£56 to £85" in this version
      var split = group.querySelector('.price-input-group-split');
      if (split) split.textContent = 'to';
    });
  }

  // "FILTER BY (104)" — the count is a real .faceted-filter-results-count span
  // (not text we write ourselves), so the site's existing live-filter code
  // keeps it in sync on every change via its own document-wide
  // querySelectorAll('.faceted-filter-results-count') update pass.
  function setHeaderText(drawer) {
    var h2 = drawer.querySelector('.filter-drawer-header h2');
    if (!h2 || h2.querySelector('.faceted-filter-results-count')) return;

    h2.textContent = 'FILTER BY ';

    var countSpan = document.createElement('span');
    countSpan.className = 'faceted-filter-results-count vone-header-count';
    countSpan.setAttribute('aria-live', 'polite');

    // Seed with whatever count already exists elsewhere in the drawer so it
    // isn't blank before the next filter change triggers the site's update.
    var existing = drawer.querySelector('.faceted-filter-results-count');
    if (existing) countSpan.innerHTML = existing.innerHTML;

    h2.appendChild(countSpan);
  }

  // Selected-value preview shown right before each group's chevron, e.g. "White ^"
  function getOrCreateValueSpan(header) {
    var span = header.querySelector(':scope > .vone-selected-value');
    if (span) return span;
    span = document.createElement('span');
    span.className = 'vone-selected-value';
    var chevron = header.querySelector(':scope > .chevron');
    if (chevron) {
      header.insertBefore(span, chevron);
    } else {
      header.appendChild(span);
    }
    return span;
  }

  function priceValueText(group) {
    var min = group.querySelector('.price-input-min');
    var max = group.querySelector('.price-input-max');
    if (!min || !max || !min.value || !max.value) return '';
    var symbol = (group.querySelector('.price-input-group span') || {}).textContent || '';
    return symbol + min.value + ' to ' + symbol + max.value;
  }

  function checkboxValueText(group) {
    var checked = Array.prototype.slice.call(
      group.querySelectorAll('.filter-option-input:checked')
    );
    if (!checked.length) return '';
    var first = checked[0].getAttribute('data-display-value') || '';
    if (checked.length === 1) return first;
    return first + ' +' + (checked.length - 1);
  }

  function updateSelectedValue(group) {
    var header = group.querySelector('.filter-drawer-group-header[data-group-header]');
    if (!header) return;
    var span = getOrCreateValueSpan(header);
    var isPrice = !!group.querySelector('.price-filter-wrapper');
    span.textContent = isPrice ? priceValueText(group) : checkboxValueText(group);
  }

  function updateAllSelectedValues(drawer) {
    drawer.querySelectorAll('.filter-drawer-group').forEach(updateSelectedValue);
  }

  function init() {
    var drawer = document.getElementById(DRAWER_ID);
    if (!drawer) return;

    // Whenever the drawer opens, expand every group and set the static header text.
    var observer = new MutationObserver(function (mutations) {
      if (!isVersionOne()) return;
      mutations.forEach(function (mutation) {
        if (mutation.attributeName === 'class' && drawer.classList.contains('open')) {
          expandAllGroups(drawer);
          setHeaderText(drawer);
          updateAllSelectedValues(drawer);
        }
      });
    });
    observer.observe(drawer, { attributes: true, attributeFilter: ['class'] });

    // Keep each header's preview in sync as filters are checked/unchecked or price changes
    drawer.addEventListener('change', function (e) {
      if (!isVersionOne()) return;
      var group = e.target.closest('.filter-drawer-group');
      if (group) updateSelectedValue(group);
    });
    drawer.addEventListener('input', function (e) {
      if (!isVersionOne()) return;
      if (!e.target.matches('.price-input-min, .price-input-max')) return;
      var group = e.target.closest('.filter-drawer-group');
      if (group) updateSelectedValue(group);
    });

    // The price slider's own init code runs in the *bubble* phase of this
    // same "toggle" event (on the <details> itself) and is what actually
    // populates the min/max inputs. toggle doesn't bubble, so delegation
    // requires capture — but capture fires before that bubble-phase init,
    // so defer the read one tick until the whole dispatch has settled.
    drawer.addEventListener(
      'toggle',
      function (e) {
        if (!isVersionOne()) return;
        var group = e.target.closest('.filter-drawer-group');
        if (group) setTimeout(function () { updateSelectedValue(group); }, 0);
      },
      true
    );

    // Intercept group header clicks before the default (exclusive) handler runs,
    // so each group toggles independently instead of closing the others.
    document.addEventListener(
      'click',
      function (e) {
        if (!isVersionOne()) return;
        var header = e.target.closest('#' + DRAWER_ID + ' .filter-drawer-group-header[data-group-header]');
        if (!header) return;

        e.stopPropagation();

        var group = header.closest('.filter-drawer-group');
        if (!group) return;

        var isOpen = group.getAttribute('drawer-group-open') === 'true';
        if (isOpen) {
          group.removeAttribute('drawer-group-open');
          header.setAttribute('aria-expanded', 'false');
        } else {
          group.setAttribute('drawer-group-open', 'true');
          header.setAttribute('aria-expanded', 'true');
        }
      },
      true
    );
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();


/* ==========================================================================
   VERSION TWO — flat list, every group permanently expanded, no collapse
   ========================================================================== */
(function () {
  var DRAWER_ID = 'custom-mobile-filter-drawer';

  function isVersionTwo() {
    return psFilterIsMobile() && document.documentElement.getAttribute('data-filter-variant') === 'v2';
  }

  function expandAllGroups(drawer) {
    drawer.querySelectorAll('.filter-drawer-group').forEach(function (group) {
      group.setAttribute('drawer-group-open', 'true');
      var details = group.querySelector('details.custom-filter-group');
      if (details && !details.open) details.open = true;
    });
  }

  function setHeaderText(drawer) {
    var h2 = drawer.querySelector('.filter-drawer-header h2');
    if (h2) h2.textContent = 'FILTER BY';
  }

  // "Show Results" -> "View Results", keeping the live count/spinner spans intact
  function setFooterText(drawer) {
    var btn = drawer.querySelector('.filter-drawer-footer .show-results-btn');
    if (!btn) return;
    var textNode = Array.prototype.slice.call(btn.childNodes).filter(function (n) {
      return n.nodeType === 3 && n.textContent.trim().length;
    })[0];
    if (textNode) textNode.textContent = ' View Results ';
  }

  function init() {
    var drawer = document.getElementById(DRAWER_ID);
    if (!drawer) return;

    // Whenever the drawer opens, expand every group and relabel header/footer.
    var observer = new MutationObserver(function (mutations) {
      if (!isVersionTwo()) return;
      mutations.forEach(function (mutation) {
        if (mutation.attributeName === 'class' && drawer.classList.contains('open')) {
          expandAllGroups(drawer);
          setHeaderText(drawer);
          setFooterText(drawer);
        }
      });
    });
    observer.observe(drawer, { attributes: true, attributeFilter: ['class'] });

    // Groups have no chevron and can't be collapsed — swallow header clicks
    // before the default (exclusive open/close) handler runs.
    document.addEventListener(
      'click',
      function (e) {
        if (!isVersionTwo()) return;
        var header = e.target.closest('#' + DRAWER_ID + ' .filter-drawer-group-header[data-group-header]');
        if (!header) return;
        e.stopPropagation();
        e.preventDefault();
      },
      true
    );
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();